/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

// Define global invariant to prevent errors with react-immutable-proptypes in production
window.invariant =
  window.invariant ||
  function () {
    return null;
  };

import React, { Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import Soundtalks from 'containers';
import { Provider, createStore, PersistGate, actions, ApiInterceptor, Sentry } from 'data';
import storage from 'utils/storage';
import AuthProvider from 'utils/authorization-provider';
import { FluidPreloader } from 'components/core/preloader';
import { GlobalErrorBoundary } from 'utils/error-boundaries';
import { HelmetProvider } from 'react-helmet-async';
import appConfig from 'config';

import 'ui/styles/global.scss';

import { getLanguage } from 'utils/language-helpers';

import './i18n';

const rootElement = document.getElementById('root');

const { store, persistor } = createStore({ storage, language: getLanguage() });

const applyThemeColors = (themeColors) => {
  if (!themeColors || typeof themeColors !== 'object') return;

  const rootStyle = document.documentElement?.style;
  if (!rootStyle) return;

  Object.entries(themeColors).forEach(([cssVarName, value]) => {
    if (typeof cssVarName !== 'string') return;
    if (typeof value !== 'string' || value.trim() === '') return;

    rootStyle.setProperty(cssVarName, value.trim());
  });
};

applyThemeColors(appConfig?.themeColors);

if (process.env.PROJECT_ENV !== 'development' && process.env.PROJECT_ENV) {
  Sentry.init({
    dsn: 'https://6aec399129a64cf4b9b6ede94537ce08@o238345.ingest.sentry.io/5219133',
    environment: process.env.PROJECT_ENV,
    // environment: 'development',
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 0.05,
    normalizeDepth: 5,
    beforeSend: (event, hint) => {
      if (hint.originalException?.response?.status === 400) return null;
      return event;
    },
  });
  Sentry.setContext('language', navigator.language);
}

const onBeforeLift = async () => {
  await store.dispatch(actions.session.refreshSession());
  return true;
};

function AppLayout() {
  return (
    <ApiInterceptor>
      <AuthProvider>
        <HelmetProvider>
          <GlobalErrorBoundary>
            <Suspense fallback={<FluidPreloader style={{ height: '100vh' }} />}>
              <Soundtalks />
            </Suspense>
          </GlobalErrorBoundary>
        </HelmetProvider>
      </AuthProvider>
    </ApiInterceptor>
  );
}

const router = createBrowserRouter([
  {
    path: '*',
    element: <AppLayout />,
  },
]);

const root = createRoot(rootElement);
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor} onBeforeLift={onBeforeLift}>
      <RouterProvider router={router} />
    </PersistGate>
  </Provider>,
);
